import {
  recordDepositDisposition,
  reverseDepositDisposition,
  useListDepositDispositions,
  type DepositDispositionOut,
} from '@myrentalmate/api-client';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { optional, required, type Field } from '../../components/FormFields';
import { okData } from '../../lib/apiData';
import { formatDate, formatMoney, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';

const FIELDS: Field[] = [
  { name: 'refunded', label: 'Return to Resident', type: 'number' },
  {
    name: 'withheld',
    label: 'Keep Against What They Owe',
    type: 'number',
    helperText: 'Add any charges for damage first. You can only keep what the lease owes',
  },
  { name: 'reason', label: 'Why Any Is Being Kept', type: 'multiline' },
  { name: 'reference', label: 'Refund Check Number' },
  { name: 'decided_on', label: 'Date', type: 'date', required: true },
];
const REVERSE_FIELDS: Field[] = [{ name: 'reason', label: 'Reason', required: true }];

export function DepositSection({
  leaseId,
  isActive,
  held,
  balance,
}: {
  leaseId: number;
  isActive: boolean;
  held: number;
  balance: number;
}) {
  const canManage = useCan('payments.manage');
  const rows = okData(useListDepositDispositions(leaseId).data)?.items ?? [];
  const [today, setToday] = useState<string | null>(null);
  const [reversing, setReversing] = useState<DepositDispositionOut | null>(null);
  const columns: DataColumn<DepositDispositionOut>[] = [
    { label: 'Date', render: (d) => formatDate(d.decided_on) },
    { label: 'Returned', render: (d) => formatMoney(d.refunded) },
    { label: 'Kept', render: (d) => formatMoney(d.withheld) },
    { label: 'Reason', render: (d) => d.reason ?? '–' },
    { label: 'Check', render: (d) => d.reference ?? '–' },
    {
      label: 'Actions',
      align: 'right',
      render: (d) =>
        d.reversed_at ? (
          <Chip size="small" color="error" label="Reversed" />
        ) : (
          <>
            <Button size="small" href={`/api/deposit-dispositions/${d.id}/letter`}>
              Letter
            </Button>
            {canManage && (
              <Button size="small" onClick={() => setReversing(d)}>
                Reverse
              </Button>
            )}
          </>
        ),
    },
  ];

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Security Deposit
        </Typography>
        {canManage && !isActive && held > 0 && (
          <Button variant="outlined" size="small" onClick={() => setToday(todayIso())}>
            Return or keep deposit
          </Button>
        )}
      </Stack>
      <Typography color="text.secondary">
        {formatMoney(held)} held.{' '}
        {isActive && held > 0 ? 'You can return or keep it once the lease has ended.' : ''}
      </Typography>
      {rows.length > 0 && <DataTable columns={columns} rows={rows} rowKey={(d) => d.id} />}
      {today && (
        <FormDialog
          title="Return or Keep the Deposit"
          intro="Your state sets deadlines and notice rules for this. Check them before you finish."
          fields={FIELDS}
          initial={{
            refunded: Math.max(held - Math.max(balance, 0), 0).toFixed(2),
            withheld: Math.min(Math.max(balance, 0), held).toFixed(2),
            reason: '',
            reference: '',
            decided_on: today,
          }}
          submitLabel="Save"
          onSubmit={(values) =>
            recordDepositDisposition(leaseId, {
              refunded: optional(values, 'refunded') ?? '0',
              withheld: optional(values, 'withheld') ?? '0',
              reason: optional(values, 'reason'),
              reference: optional(values, 'reference'),
              decided_on: required(values, 'decided_on'),
            })
          }
          onClose={() => setToday(null)}
        />
      )}
      {reversing && (
        <FormDialog
          title="Reverse This?"
          intro="The deposit and what the lease owes go back to how they were. This stays on record."
          fields={REVERSE_FIELDS}
          initial={{ reason: '' }}
          submitLabel="Reverse"
          onSubmit={(values) =>
            reverseDepositDisposition(reversing.id, { reason: required(values, 'reason') })
          }
          onClose={() => setReversing(null)}
        />
      )}
    </Stack>
  );
}
